import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@ais-ui/utils';

const LIFT_VARIANTS = new Set([
  'default',
  'destructive',
  'oceanio',
  'oceanioDestructive',
]);

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md md:text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 hover:shadow-[var(--shadow-primary)]',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 hover:-translate-y-0.5 hover:shadow-[0_4px_14px_hsl(var(--destructive)/0.3)] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:shadow-[var(--shadow-sm)] dark:bg-input/30 dark:border-input dark:hover:bg-input/50 transition-colors ease-out',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-[var(--shadow-xs)] transition-colors ease-out',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 transition-colors ease-out',
        link: 'text-primary underline-offset-4 hover:underline transition-colors ease-out',
        oceanio:
          'rounded bg-[#00647A] hover:bg-[#3A899B] hover:-translate-y-0.5 hover:shadow-[0_4px_14px_rgba(0,100,122,0.3)] text-white focus-visible:border-none',
        oceanioDestructive:
          'rounded bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:-translate-y-0.5 focus-visible:border-none',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: 'h-4 rounded-md px-1 text-xs',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        smIcon: 'size-8',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      data-lift={LIFT_VARIANTS.has(variant ?? 'default') || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
